import Elk, { ElkNode, ElkExtendedEdge } from "elkjs"
import { Edge, Node } from "@vue-flow/core"

export interface LayoutGroup {
	id: string
	nodeIds: Set<string>
}

export const layout = async (nodes: Node[], edges: Edge[], direction: string, groups?: LayoutGroup[], compact = false) => {
	const elk = new Elk()

	// Build ELK node lookup
	const toElkNode = (n: Node): ElkNode => ({ id: n.id, width: (n.width as number) || 75, height: (n.height as number) || 45 })

	let elkChildren: ElkNode[]
	let elkEdges: ElkExtendedEdge[]
	const groupedNodeIds = new Set<string>()

	if (groups && groups.length > 0) {
		// Collect all grouped node IDs
		for (const g of groups) {
			for (const nid of g.nodeIds) groupedNodeIds.add(nid)
		}

		// Create compound parent nodes for each group
		const compoundNodes: ElkNode[] = groups.map((g) => {
			const children = nodes.filter((n) => g.nodeIds.has(n.id)).map(toElkNode)
			return {
				id: g.id,
				layoutOptions: {
					"elk.algorithm": "layered",
					"elk.direction": direction,
					"elk.padding": "[top=30,left=15,bottom=15,right=15]",
				},
				children,
			}
		})

		// Ungrouped nodes
		const ungroupedNodes = nodes.filter((n) => !groupedNodeIds.has(n.id)).map(toElkNode)
		elkChildren = [...ungroupedNodes, ...compoundNodes]

		// Map edges: if source/target is inside a group, use hierarchical port references
		elkEdges = edges.map((e): ElkExtendedEdge => ({ id: e.id, sources: [e.source], targets: [e.target] }))
	} else {
		elkChildren = nodes.map(toElkNode)
		elkEdges = edges.map((e): ElkExtendedEdge => ({ id: e.id, sources: [e.source], targets: [e.target] }))
	}

	const graph: ElkNode = {
		id: "root",
		layoutOptions: {
			"elk.algorithm": "layered",
			"elk.direction": direction,
			"elk.spacing.nodeNode": "18",
			"elk.layered.spacing.nodeNodeBetweenLayers": "25",
			"elk.layered.spacing.edgeNodeBetweenLayers": "10",
			"elk.layered.spacing.edgeEdgeBetweenLayers": "10",
			"elk.layered.nodePlacement.favorStraightEdges": "false",
			"elk.spacing.componentComponent": "30",
			"elk.hierarchyHandling": groups && groups.length > 0 ? "INCLUDE_CHILDREN" : "SEPARATE_CHILDREN",
		},
		children: elkChildren,
		edges: elkEdges,
	}

	const graphComputed = await elk.layout(graph)

	// Flatten results: extract positions for all nodes (including those inside compound groups)
	const nodesComputed: Node[] = []
	const processChildren = (children: ElkNode[] | undefined, offsetX = 0, offsetY = 0) => {
		if (!children) return
		for (const c of children) {
			const node = nodes.find((n) => n.id === c.id)
			if (node) {
				// This is an actual node
				nodesComputed.push({
					...node,
					width: c.width,
					height: c.height,
					position: {
						x: (c.x ?? 0) + offsetX,
						y: (c.y ?? 0) + offsetY,
					},
				} as Node)
			}
			// Recurse into compound node children
			if (c.children && c.children.length > 0) {
				processChildren(c.children, (c.x ?? 0) + offsetX, (c.y ?? 0) + offsetY)
			}
		}
	}
	processChildren(graphComputed.children)

	// Compact mode: stack disconnected components vertically
	if (compact && nodesComputed.length > 0) {
		stackComponentsVertically(nodesComputed, edges)
	}

	return {
		nodes: nodesComputed,
		edges: edges,
	}
}

/**
 * Creates a group/box node around specified nodes
 * @param nodes - Array of all positioned nodes
 * @param nodeIdsToBox - Set of node IDs that should be inside the box
 * @param groupLabel - Label for the group node
 * @returns Updated nodes array with group node added
 */
// Builds a single rectilinear SVG path string tracing the outer boundary of the given nodes.
// Nodes are grouped into horizontal rows (by overlapping y-ranges), then a clockwise polygon
// is constructed with orthogonal steps at each row boundary where the width changes.
// This produces the correct L-shape, staircase, etc. for any node arrangement without
// including non-selected nodes that happen to fall inside a simple bounding box.
export function computeHighlightPath(nodes: Node[], highlightedIds: Set<string>): string {
	const padding = 10
	const paddingRight = padding
	const paddingTop = padding
	const cornerRadius = 8
	const selected = nodes.filter((n) => highlightedIds.has(n.id))
	if (selected.length === 0) return ""

	const dw = 75
	const dh = 45

	// Check if the simple bounding rect of all selected nodes would engulf any non-selected node.
	// If no non-selected node's centre falls inside the bounding rect, a plain rounded rectangle
	// is sufficient and produces the fewest possible sides.
	const selX1 = Math.min(...selected.map((n) => n.position.x))
	const selY1 = Math.min(...selected.map((n) => n.position.y))
	const selX2 = Math.max(...selected.map((n) => n.position.x + ((n.width as number) || dw)))
	const selY2 = Math.max(...selected.map((n) => n.position.y + ((n.height as number) || dh)))

	const engulfsNonSelected = nodes.some((n) => {
		if (highlightedIds.has(n.id)) return false
		const cx = n.position.x + ((n.width as number) || dw) / 2
		const cy = n.position.y + ((n.height as number) || dh) / 2
		return cx >= selX1 && cx <= selX2 && cy >= selY1 && cy <= selY2
	})

	if (!engulfsNonSelected) {
		return buildRoundedPath(
			[
				[selX1 - padding, selY1 - paddingTop],
				[selX2 + paddingRight, selY1 - paddingTop],
				[selX2 + paddingRight, selY2 + padding],
				[selX1 - padding, selY2 + padding],
			],
			cornerRadius
		)
	}

	// Sort by original y position so row grouping is order-stable
	const sorted = [...selected].sort((a, b) => a.position.y - b.position.y)

	// Group into rows using ORIGINAL node boundaries (no padding) so that adjacent rows with
	// a small gap are never incorrectly merged due to padding expansion.
	interface RowBounds {
		x1: number
		y1: number
		x2: number
		y2: number
	}
	const rows: RowBounds[] = []
	let group = [sorted[0]]

	for (let i = 1; i < sorted.length; i++) {
		const n = sorted[i]
		const groupY2 = Math.max(...group.map((g) => g.position.y + ((g.height as number) || dh)))
		if (n.position.y < groupY2) {
			group.push(n)
		} else {
			rows.push({
				x1: Math.min(...group.map((g) => g.position.x)),
				y1: Math.min(...group.map((g) => g.position.y)),
				x2: Math.max(...group.map((g) => g.position.x + ((g.width as number) || dw))),
				y2: Math.max(...group.map((g) => g.position.y + ((g.height as number) || dh))),
			})
			group = [n]
		}
	}
	rows.push({
		x1: Math.min(...group.map((g) => g.position.x)),
		y1: Math.min(...group.map((g) => g.position.y)),
		x2: Math.max(...group.map((g) => g.position.x + ((g.width as number) || dw))),
		y2: Math.max(...group.map((g) => g.position.y + ((g.height as number) || dh))),
	})

	// Padded version of each row for the outer edges
	const pad = rows.map((r) => ({ x1: r.x1 - padding, y1: r.y1 - paddingTop, x2: r.x2 + paddingRight, y2: r.y2 + padding }))

	// For steps between rows, use the padded bottom of the current row so the step border
	// respects the same padding constant as every other edge.
	const stepY = (i: number) => pad[i].y2

	const pts: [number, number][] = []

	pts.push([pad[0].x1, pad[0].y1])
	pts.push([pad[0].x2, pad[0].y1])

	// Right side: top → bottom
	for (let i = 0; i < rows.length; i++) {
		const next = rows[i + 1]
		if (!next) {
			pts.push([pad[i].x2, pad[i].y2])
		} else if (pad[i].x2 !== pad[i + 1].x2) {
			const sy = stepY(i)
			pts.push([pad[i].x2, sy])
			pts.push([pad[i + 1].x2, sy])
		}
	}

	// Bottom edge
	pts.push([pad[pad.length - 1].x1, pad[pad.length - 1].y2])

	// Left side: bottom → top
	for (let i = rows.length - 1; i > 0; i--) {
		if (pad[i].x1 !== pad[i - 1].x1) {
			const sy = stepY(i - 1)
			pts.push([pad[i].x1, sy])
			pts.push([pad[i - 1].x1, sy])
		}
	}

	return buildRoundedPath(pts, cornerRadius)
}

// Converts a list of polygon vertices into an SVG path with rounded corners.
// Uses arc segments (A command) at each vertex, clamping the radius to fit within adjacent edges.
// In SVG screen coordinates (Y-axis down), a positive 2D cross product indicates a clockwise
// (right) turn, which corresponds to a convex outer corner — rendered with sweep-flag=1.
function buildRoundedPath(points: [number, number][], radius: number): string {
	const n = points.length
	if (n < 3) return ""

	const d: string[] = []

	for (let i = 0; i < n; i++) {
		const prev = points[(i - 1 + n) % n]
		const curr = points[i]
		const next = points[(i + 1) % n]

		const e1x = curr[0] - prev[0]
		const e1y = curr[1] - prev[1]
		const e2x = next[0] - curr[0]
		const e2y = next[1] - curr[1]
		const l1 = Math.hypot(e1x, e1y)
		const l2 = Math.hypot(e2x, e2y)

		if (l1 === 0 || l2 === 0) continue

		const r = Math.min(radius, l1 / 2, l2 / 2)
		const u1x = e1x / l1
		const u1y = e1y / l1
		const u2x = e2x / l2
		const u2y = e2y / l2

		// Entry point: r units before the corner along the incoming edge
		const ex = curr[0] - u1x * r
		const ey = curr[1] - u1y * r
		// Exit point: r units after the corner along the outgoing edge
		const ox = curr[0] + u2x * r
		const oy = curr[1] + u2y * r

		// Positive cross product = clockwise (right) turn in screen coords = convex corner → sweep 1
		const cross = e1x * e2y - e1y * e2x
		const sweep = cross > 0 ? 1 : 0

		if (i === 0) {
			d.push(`M ${ex} ${ey}`)
		} else {
			d.push(`L ${ex} ${ey}`)
		}
		d.push(`A ${r} ${r} 0 0 ${sweep} ${ox} ${oy}`)
	}

	d.push("Z")
	return d.join(" ")
}

// Traverse edges backwards from a node to find all direct/indirect ancestors (including the node itself)

export function getAncestorNodeIds(nodeId: string, edges: Edge[]): Set<string> {
	const reverseAdj = new Map<string, string[]>()
	for (const edge of edges) {
		if (!reverseAdj.has(edge.target)) reverseAdj.set(edge.target, [])
		reverseAdj.get(edge.target)!.push(edge.source)
	}

	const visited = new Set<string>()
	const queue = [nodeId]
	while (queue.length > 0) {
		const current = queue.pop()!
		if (visited.has(current)) continue
		visited.add(current)
		for (const parent of reverseAdj.get(current) || []) {
			if (!visited.has(parent)) queue.push(parent)
		}
	}
	return visited
}

export const addGroupBox = (nodes: Node[], nodeIdsToBox: Set<string>, groupLabel: string = ""): Node[] => {
	if (nodeIdsToBox.size === 0 || nodes.length === 0) {
		return nodes
	}

	const nodesInBox = nodes.filter((n) => nodeIdsToBox.has(n.id))

	if (nodesInBox.length === 0) {
		return nodes
	}

	// Calculate bounding box with padding
	const padding = 10
	const nodeWidth = 75 // default node width
	const nodeHeight = 45 // default node height

	let minX = Infinity
	let minY = Infinity
	let maxX = -Infinity
	let maxY = -Infinity

	nodesInBox.forEach((node) => {
		const width = typeof node.width === "number" ? node.width : nodeWidth
		const height = typeof node.height === "number" ? node.height : nodeHeight
		const x = node.position.x
		const y = node.position.y
		minX = Math.min(minX, x)
		minY = Math.min(minY, y)
		maxX = Math.max(maxX, x + width)
		maxY = Math.max(maxY, y + height)
	})

	// Create parent/group node with calculated dimensions
	const groupNode: Node = {
		id: "selected-steps-group",
		type: "group",
		data: { label: groupLabel },
		position: { x: minX - padding, y: minY - padding },
		style: {
			width: `${maxX - minX + padding * 2}px`,
			height: `${maxY - minY + padding * 2}px`,
			backgroundColor: "rgba(59, 130, 246, 0.08)",
			border: "3px solid #3b82f6",
			borderRadius: "8px",
		},
	}

	// Add the group node to the beginning (so it renders behind other nodes)
	return [groupNode, ...nodes]
}
// Find connected components using union-find, then reposition them vertically
function stackComponentsVertically(nodes: Node[], edges: Edge[]) {
	const nodeIds = nodes.map((n) => n.id)
	const idToIndex = new Map<string, number>()
	nodeIds.forEach((id, i) => idToIndex.set(id, i))

	// Union-Find
	const parent = nodeIds.map((_, i) => i)
	const find = (x: number): number => {
		while (parent[x] !== x) {
			parent[x] = parent[parent[x]]
			x = parent[x]
		}
		return x
	}
	const union = (a: number, b: number) => {
		const ra = find(a),
			rb = find(b)
		if (ra !== rb) parent[ra] = rb
	}

	// Connect nodes that share edges
	for (const e of edges) {
		const si = idToIndex.get(e.source)
		const ti = idToIndex.get(e.target)
		if (si !== undefined && ti !== undefined) union(si, ti)
	}

	// Group nodes by component
	const components = new Map<number, Node[]>()
	for (let i = 0; i < nodes.length; i++) {
		const root = find(i)
		if (!components.has(root)) components.set(root, [])
		components.get(root)!.push(nodes[i])
	}

	if (components.size <= 1) return // Nothing to rearrange

	// Calculate bounding box per component and sort by min-Y (preserve original vertical order)
	const componentList = Array.from(components.values()).map((compNodes) => {
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity
		for (const n of compNodes) {
			const x = n.position?.x ?? 0
			const y = n.position?.y ?? 0
			const w = (n.width as number) || 75
			const h = (n.height as number) || 45
			minX = Math.min(minX, x)
			minY = Math.min(minY, y)
			maxX = Math.max(maxX, x + w)
			maxY = Math.max(maxY, y + h)
		}
		return { nodes: compNodes, minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY }
	})

	componentList.sort((a, b) => a.minY - b.minY)

	// Stack components vertically, centering each horizontally
	const maxWidth = Math.max(...componentList.map((c) => c.width))
	const gap = 75
	let currentY = 0

	for (const comp of componentList) {
		const offsetX = (maxWidth - comp.width) / 2 - comp.minX
		const offsetY = currentY - comp.minY
		for (const n of comp.nodes) {
			n.position = {
				x: (n.position?.x ?? 0) + offsetX,
				y: (n.position?.y ?? 0) + offsetY,
			}
		}
		currentY += comp.height + gap
	}
}
